import { sampleBilinear, type GridSpec, type ScalarField } from '../sim/grid.ts';
import type { Grain } from '../sim/snowpack.ts';
import type { AvalancheProblem } from '../sim/stability.ts';

/**
 * Geometry helpers for the 3D scene's hover probe: converting between UTM metres and the
 * mesh's local coordinate system, and ray-marching the terrain+snow heightfield to find the
 * point under the cursor without a triangle raycast against the (potentially ~1M-vertex) mesh.
 */

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export interface Ray {
  origin: Vec3;
  dir: Vec3;
}

export interface Box3 {
  min: Vec3;
  max: Vec3;
}

/** Mesh-space origin (in UTM metres) — the grid's geometric centre, x = east, z = −north. */
export interface MeshOrigin {
  cx: number;
  cy: number;
}

export function meshOrigin(spec: GridSpec): MeshOrigin {
  const width = spec.cols * spec.cellSize;
  const height = spec.rows * spec.cellSize;
  return { cx: spec.minX + width / 2, cy: spec.maxY - height / 2 };
}

export function utmToMesh(origin: MeshOrigin, xUtm: number, yUtm: number): [number, number] {
  return [xUtm - origin.cx, -(yUtm - origin.cy)];
}

export function meshToUtm(origin: MeshOrigin, xMesh: number, zMesh: number): [number, number] {
  return [xMesh + origin.cx, origin.cy - zMesh];
}

/** Nearest terrain cell to a UTM position, clamped to the grid. */
export function nearestCell(
  spec: GridSpec,
  xUtm: number,
  yUtm: number,
): { col: number; row: number } {
  const col = Math.min(spec.cols - 1, Math.max(0, Math.floor((xUtm - spec.minX) / spec.cellSize)));
  const row = Math.min(spec.rows - 1, Math.max(0, Math.floor((spec.maxY - yUtm) / spec.cellSize)));
  return { col, row };
}

function slab(o: number, d: number, lo: number, hi: number, range: [number, number]): boolean {
  if (Math.abs(d) < 1e-12) return o >= lo && o <= hi;
  let t1 = (lo - o) / d;
  let t2 = (hi - o) / d;
  if (t1 > t2) {
    const tmp = t1;
    t1 = t2;
    t2 = tmp;
  }
  if (t1 > range[0]) range[0] = t1;
  if (t2 < range[1]) range[1] = t2;
  return range[0] <= range[1];
}

/** Ray–AABB slab intersection; returns [tEntry, tExit] along the ray, or null if it misses. */
export function intersectBox(ray: Ray, box: Box3): [number, number] | null {
  const range: [number, number] = [-Infinity, Infinity];
  if (!slab(ray.origin.x, ray.dir.x, box.min.x, box.max.x, range)) return null;
  if (!slab(ray.origin.y, ray.dir.y, box.min.y, box.max.y, range)) return null;
  if (!slab(ray.origin.z, ray.dir.z, box.min.z, box.max.z, range)) return null;
  return range;
}

/**
 * Ray-march a heightfield: step forward by `step` along the ray until it dips below the
 * surface, then bisect between the last two samples for a refined hit point. Returns null if
 * the ray misses the bounding box or never crosses the surface within it.
 */
export function marchHeightfield(
  ray: Ray,
  heightAt: (x: number, z: number) => number,
  box: Box3,
  step: number,
  maxSteps = 4000,
): Vec3 | null {
  const range = intersectBox(ray, box);
  if (!range) return null;
  const tEnd = range[1];
  const tStart = Math.max(range[0], 0);
  if (tStart > tEnd || step <= 0) return null;

  const pointAt = (t: number): Vec3 => ({
    x: ray.origin.x + ray.dir.x * t,
    y: ray.origin.y + ray.dir.y * t,
    z: ray.origin.z + ray.dir.z * t,
  });
  const diffAt = (t: number): number => {
    const p = pointAt(t);
    return p.y - heightAt(p.x, p.z);
  };

  if (diffAt(tStart) <= 0) return pointAt(tStart);

  let prevT = tStart;
  let curT = tStart + step;
  let steps = 0;
  while (curT <= tEnd && steps < maxSteps) {
    if (diffAt(curT) <= 0) {
      let lo = prevT;
      let hi = curT;
      for (let i = 0; i < 12; i++) {
        const mid = (lo + hi) / 2;
        if (diffAt(mid) > 0) lo = mid;
        else hi = mid;
      }
      return pointAt(hi);
    }
    prevT = curT;
    curT += step;
    steps++;
  }
  return null;
}

/** Bilinear surface height (elevation + snow depth, exaggerated) matching the terrain mesh. */
export function makeHeightSampler(
  elevation: ScalarField,
  snowDepthOnTerrain: ScalarField,
  minElevation: number,
  exaggeration: number,
  origin: MeshOrigin,
): (xMesh: number, zMesh: number) => number {
  return (xMesh, zMesh) => {
    const [xUtm, yUtm] = meshToUtm(origin, xMesh, zMesh);
    const elev = sampleBilinear(elevation, xUtm, yUtm);
    const snow = sampleBilinear(snowDepthOnTerrain, xUtm, yUtm);
    return (elev - minElevation + Math.max(0, snow)) * exaggeration;
  };
}

export interface HoverSample {
  xUtm: number;
  yUtm: number;
  /** m */
  elevation: number;
  /** deg */
  slope: number;
  /** deg clockwise from north; NaN when flat */
  aspect: number;
  /** m, total snow depth (terrain grid) */
  snowDepth: number;
  /** Number of layers in the snowpack-grid column under the cursor. */
  layerCount?: number;
  /** Grain class of the top layer, if any. */
  topGrain?: Grain;
  /** m, storm snowfall (before wind redistribution). Only when a storm result exists. */
  stormSnowfall?: number;
  /** m, net wind change: + loaded / − scoured. Only when a storm result exists. */
  windChange?: number;
  /** m/s. Only when a storm result exists. */
  windSpeed?: number;
  /** Grid bearing (deg) the wind blows from. Only when a storm result exists. */
  windFromGridDeg?: number;
  /** Winstral Sx (deg). Only when a storm result exists. */
  sx?: number;
  /** mm. Only when a storm result exists. */
  rain?: number;
  /** Index into the snowpack grid under the cursor, when a snowpack result exists. */
  snowpackCell?: number;
  /** SK38 skier stability index of the critical layer (capped at 9.99). */
  skier?: number;
  /** Avalanche problem at this point. */
  problem?: AvalancheProblem;
  /** Grain of the critical (weakest) layer, if there is a slab over one. */
  criticalGrain?: Grain;
  /** Depth (m, vertical) of the critical layer's top below the surface. */
  criticalDepth?: number;
  /** m, cumulative avalanche debris depth here (all runs). Only when > ~0. */
  avDebrisDepth?: number;
  /** m, cumulative snow removed here (all runs). Only when > ~0. */
  avRemovedDepth?: number;
  /** m, cumulative net snow-depth change here (all runs). Only when avalanche data exists. */
  avNetChange?: number;
  /** %, share of the selected run's deposit here that came from entrainment. */
  avOriginSharePct?: number;
  /** m/s, the selected run's max speed reached here. */
  avMaxSpeed?: number;
  /** kPa, the selected run's max impact pressure here. */
  avMaxPressureKPa?: number;
}
